import { useTitle } from '@flippingbook/web.react-hooks/dist/useTitle';
import { Button, LinkButton } from '@flippingbook/web.react-kit/dist/Button';
import { L10n } from '@flippingbook/web.react-kit/dist/L10n';
import cx from 'classnames';
import { useCallback, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { useRegisterPage } from '@/hooks/useRegisterPage';
import { loc } from '@/localization';
import { analyticsService } from '@/services/instance/analytics';
import { discoveryService } from '@/services/instance/discovery';
import { optionsService } from '@/services/instance/optionsService';
import { Field } from '@/site/components/Field';
import { Link } from '@/site/components/Link';
import { Separator } from '@/site/components/Separator';
import { UserPasswordField } from '@/site/components/UserPasswordField';
import { Captcha } from '@/site/pages/Register/Captcha';
import { PublisherTeaser } from '@/site/pages/Register/PublisherTeaser';
import { SideArea } from '@/site/pages/Register/SideArea';
import { TitleAndDescription } from '@/site/pages/Register/TitleAndDescription';
import { getProviderUrl } from '@/utils/getProviderUrl';

import Logo from '../../components/Header/assets/online.svg?react';
import googleIcon from '../Login/assets/google.svg';

import { ErrorBox } from './ErrorBox';
import { Policies } from './policies';
import { b, e, m } from './style.css';

const onEmailInputStart = () => analyticsService.sendInputStartEvent('email');
const onPasswordInputStart = () => analyticsService.sendInputStartEvent('password');
const onFirstNameInputStart = () => analyticsService.sendInputStartEvent('first_name');
const onLastNameInputStart = () => analyticsService.sendInputStartEvent('last_name');

const onSubmitClick = () => analyticsService.sendRegisterButtonClick();

const onLogin = () => analyticsService.sendLoginEvent('register_form');
const onRegister = () => analyticsService.sendRegisterEvent();
const onShowCaptcha = (attempts: number) => analyticsService.sendShowCaptchaEvent(attempts);

const CAPTCHA_WIDTH = 180;
const CAPTCHA_HEIGHT = 40;

const logoLink = discoveryService.get('site');

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export function RegisterPage() {
    useTitle(loc.Main.AccountPageTitle.replace('{0}', loc.Registration.SignUpTitle));

    const {
        isFBOnline,
        isPublisher2,
        hasLoginError,
        loginUrl,
        restoreUrl,
        watch,
        register,
        errors,
        isSubmitting,
        isSubmitted,
        captchaImageSrc,
        refreshCaptcha,
        onFormSubmit,
        validationRules,
        productLink,
        preventLinkActionOnFormProgress,
        triggerValidation,
    } = useRegisterPage({
        captchaWidth: CAPTCHA_WIDTH,
        captchaHeight: CAPTCHA_HEIGHT,
        onLogin,
        onRegister,
        onShowCaptcha,
    });

    const externalLoginFailureReason = optionsService.getExternalLoginFailureReason();
    const hasPolicyError = externalLoginFailureReason === 'DeclinedByPolicy';
    const submitButtonTitle = isFBOnline
        ? loc.Registration.DoStartTrialFBOnline
        : isPublisher2
          ? loc.Registration.DoStartTrialPublisher2
          : loc.Registration.DoStartTrialShort;
    const className = cx(b.name, {
        [b.m.isFBOnline]: isFBOnline,
        [b.m.isPublisher]: isPublisher2,
        [b.m.safari]: isSafari,
    });

    const handleGoogleClick = useCallback(
        (ev: React.MouseEvent<HTMLAnchorElement>) => {
            preventLinkActionOnFormProgress(ev);

            if (!ev.defaultPrevented) {
                void analyticsService.sendExternalLoginButtonClick('google');
            }
        },
        [preventLinkActionOnFormProgress],
    );

    const emailErrorType = errors.email?.type;
    useEffect(() => {
        if (emailErrorType) {
            void analyticsService.sendEmailInvalidEvent();
        }
    }, [emailErrorType]);

    const passwordErrorType = errors.password?.type;
    useEffect(() => {
        if (passwordErrorType) {
            void analyticsService.sendPasswordInvalidEvent();
        }
    }, [passwordErrorType]);

    if (hasPolicyError) {
        return <Navigate to={loginUrl} />;
    }

    return (
        <div className={className}>
            <div className={e.sideArea}>
                <SideArea
                    className={e.sideContent}
                    logoLink={logoLink}
                />
                {isPublisher2 && <PublisherTeaser productLink={productLink} />}
            </div>
            <div className={e.mainArea}>
                <a
                    className={e.logo}
                    href={logoLink}
                >
                    <Logo />
                </a>
                <TitleAndDescription
                    isPublisher2={isPublisher2}
                    isFBOnline={isFBOnline}
                />
                {hasLoginError && (
                    <ErrorBox
                        loginUrl={loginUrl}
                        restoreUrl={restoreUrl}
                    />
                )}
                <div className={e.content}>
                    <form
                        onSubmit={onFormSubmit}
                        noValidate
                    >
                        <div className={e.pairedFields}>
                            <Field
                                name="firstName"
                                type="text"
                                className={e.firstNameField}
                                placeholder={loc.Registration.FirstName}
                                autoFocus
                                register={register}
                                errors={errors}
                                validationRules={validationRules}
                                readOnly={isSubmitting}
                                onStartTyping={onFirstNameInputStart}
                            />
                            <Field
                                name="lastName"
                                type="text"
                                className={e.lastNameField}
                                placeholder={loc.Registration.LastName}
                                register={register}
                                errors={errors}
                                validationRules={validationRules}
                                readOnly={isSubmitting}
                                onStartTyping={onLastNameInputStart}
                            />
                        </div>
                        <Field
                            name="email"
                            type="email"
                            className={e.emailField}
                            placeholder={`${loc.Login.WorkEmail} *`}
                            register={register}
                            errors={errors}
                            validationRules={validationRules}
                            readOnly={isSubmitting}
                            onStartTyping={onEmailInputStart}
                        />
                        <UserPasswordField
                            name="password"
                            placeholder={`${loc.Login.Password} *`}
                            register={register}
                            errors={errors}
                            validationRules={validationRules}
                            readOnly={isSubmitting}
                            isSubmitted={isSubmitted}
                            watch={watch}
                            triggerValidation={triggerValidation}
                            onStartTyping={onPasswordInputStart}
                        />
                        {captchaImageSrc && (
                            <Captcha
                                refreshCaptcha={refreshCaptcha}
                                captchaImageSrc={captchaImageSrc}
                                captchaImageWidth={CAPTCHA_WIDTH}
                                captchaImageHeight={CAPTCHA_HEIGHT}
                                register={register}
                                errors={errors}
                                validationRules={validationRules}
                                isSubmitting={isSubmitting}
                            />
                        )}
                        <Button
                            className={e.submitButton}
                            type="submit"
                            theme="green"
                            size="large"
                            progress={isSubmitting}
                            disabled={isSubmitting}
                            onClick={onSubmitClick}
                        >
                            {submitButtonTitle}
                        </Button>
                    </form>
                    <Separator className={e.separator} />
                    <div className={e.externalLoginButtonWrapper}>
                        <LinkButton
                            className={cx(e.externalLoginButton, {
                                [m.externalLoginButton.disabled]: isSubmitting,
                            })}
                            onClick={handleGoogleClick}
                            href={getProviderUrl('google')}
                            size="large"
                            theme="black"
                            variant="outline"
                            startIcon={
                                <img
                                    className={e.buttonIcon}
                                    src={googleIcon}
                                    alt="Google"
                                />
                            }
                        >
                            {loc.Registration.SignUpWith} Google
                        </LinkButton>
                    </div>
                    <p className={e.alreadyHasAccount}>
                        {loc.Registration.RegisterHasAccount} <Link to={loginUrl}>{loc.Login.DoLogin}</Link>
                    </p>

                    <div className={e.privacyNotice}>
                        <L10n
                            text={loc.Login.PrivacyNotice}
                            replaces={[
                                [
                                    '{0}',
                                    <Policies
                                        isFBOnline={isFBOnline}
                                        isPublisher2={isPublisher2}
                                    />,
                                ],
                            ]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
